import React, { useEffect, useRef, useState } from 'react'
import { ClientService } from '../../component/services/client.service'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUniversity, faDownload, faSearch, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { Col, Row } from 'react-bootstrap'
import Select from 'react-select'
import CetLoader from '../../component/Loader/CetLoader'
import ReverificationTable from '../../component/table/Reverification.table'
import TablePaginationComponent from '../../component/table/TablePagination'
import toast from 'react-hot-toast'
import moment from 'moment'
import { useTheme } from '../../theme/useTheme'

type TabKey = 'both_matched' | 'only_fingerprint' | 'only_facial' | 'none_matched' | 'absent'

interface FilterOption {
    label: string
    value: string
}

const TABS: { key: TabKey; label: string; summaryKey: string; accent: string; dotColor: string }[] = [
    { key: 'both_matched',      label: 'Both Matched',            summaryKey: 'bothMatched',      accent: '#10b981', dotColor: '#10b981' },
    { key: 'only_fingerprint',  label: 'Fingerprint Only',        summaryKey: 'onlyFingerprint',  accent: '#3b82f6', dotColor: '#3b82f6' },
    { key: 'only_facial',       label: 'Face Only',               summaryKey: 'onlyFacial',       accent: '#8b5cf6', dotColor: '#8b5cf6' },
    { key: 'none_matched',      label: 'Both Not Matched',        summaryKey: 'noneMatched',      accent: '#ef4444', dotColor: '#ef4444' },
    { key: 'absent',            label: 'Absent',                  summaryKey: 'absent',           accent: '#f59e0b', dotColor: '#f59e0b' },
]

export default function ReverificationsDetails() {
    const { colors } = useTheme()
    const [streams, setStreams] = useState<any>([])
    const [selectedStreams, setSelectedStreams] = useState<any>()
    const [selectedCollege, setSelectedCollege] = useState<any>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [colleges, setColleges] = useState<any[]>([])
    const [activeTab, setActiveTab] = useState<TabKey>('both_matched')
    const [search, setSearch] = useState<string>('')
    const [limit, setLimit] = useState<number>(10)
    const [csvLoading, setCsvLoading] = useState<boolean>(false)

    const [tabData, setTabData] = useState<Record<TabKey, any[]>>({
        both_matched: [], only_fingerprint: [], only_facial: [], none_matched: [], absent: []
    })
    const [tabPage, setTabPage] = useState<Record<TabKey, number>>({
        both_matched: 1, only_fingerprint: 1, only_facial: 1, none_matched: 1, absent: 1
    })
    const [tabTotal, setTabTotal] = useState<Record<TabKey, number>>({
        both_matched: 0, only_fingerprint: 0, only_facial: 0, none_matched: 0, absent: 0
    })
    const [summary, setSummary] = useState<any>(null)

    const searchTimer = useRef<any>(null)
    const collegesRef = useRef<any>(null)
    const streamsRef = useRef<any>(undefined)
    const limitRef = useRef<number>(10)
    const searchRef = useRef<string>('')
    const activeTabRef = useRef<TabKey>('both_matched')

    const areSelectionsEqual = (a: FilterOption[] = [], b: FilterOption[] = []) => {
        if (a.length !== b.length) return false
        return a.map(o => o.value).sort().join(',') === b.map(o => o.value).sort().join(',')
    }

    const fetchTab = async (tab: TabKey, p: number, l: number, s: string, colleges: any, streams: any) => {
        setLoading(true)
        const payload = {
            colleges: colleges?.map((d: any) => d?.value) || [],
            streams: streams?.map((d: any) => d?.value) || [],
        }
        try {
            const res = await ClientService.getAllUsersReverifications(payload, p, l, s, tab)
            if (res.status === 200) {
                const responseData = res.data?.data || {}
                const pagination = responseData?.pagination || {}
                const records = responseData?.records || []
                setTabData(prev => ({ ...prev, [tab]: records }))
                setTabPage(prev => ({ ...prev, [tab]: Number(pagination.page) || p }))
                setTabTotal(prev => ({ ...prev, [tab]: Number(pagination.totalRecords) || 0 }))
                setSummary(responseData?.summary || null)
                limitRef.current = l
                setLimit(l)
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to fetch data')
        } finally {
            setLoading(false)
        }
    }

    const getAllStreams = async () => {
        const res = await ClientService.getAllStreams()
        if (res.status === 200) {
            setStreams(res.data.streams.map((d: any) => ({ label: d, value: d })))
        }
    }

    async function fetchColleges() {
        setLoading(true)
        try {
            const res: any = await ClientService.getReverificationColleges()
            if (res.status === 200) {
                setColleges(res.data?.data?.map((c: any) => ({ value: c?.collegId, label: c?.collegeName })) || [])
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to fetch colleges')
        } finally {
            setLoading(false)
        }
    }

    const emptyTabState = () => ({
        both_matched: [], only_fingerprint: [], only_facial: [], none_matched: [], absent: [] as any[]
    })
    const emptyTabNums = () => ({
        both_matched: 0, only_fingerprint: 0, only_facial: 0, none_matched: 0, absent: 0
    })

    const resetAndFetch = (colleges: any, streams: any, search: string, limit: number) => {
        setTabPage({ both_matched: 1, only_fingerprint: 1, only_facial: 1, none_matched: 1, absent: 1 })
        setTabData(emptyTabState())
        setSummary(null)
        fetchTab(activeTabRef.current, 1, limit, search, colleges, streams)
    }

    const handleCollegesChange = (selectedOptions: any) => {
        let newSelected: FilterOption[] = selectedOptions?.some((o: any) => o.value === 'All')
            ? colleges.map(c => ({ label: c.label, value: c.value }))
            : selectedOptions || []
        if (areSelectionsEqual(collegesRef.current || [], newSelected)) return
        collegesRef.current = newSelected
        setSelectedCollege(newSelected)
        if (!newSelected.length && !streamsRef.current?.length) {
            setTabData(emptyTabState()); setTabTotal(emptyTabNums()); setSummary(null); return
        }
        resetAndFetch(newSelected, streamsRef.current, searchRef.current, limitRef.current)
    }

    const handleStreamChange = (selectedOptions: any) => {
        let newSelected: FilterOption[] = selectedOptions?.some((o: any) => o.value === 'All')
            ? streams.map((s: any) => ({ label: s.label, value: s.value }))
            : selectedOptions || []
        if (areSelectionsEqual(streamsRef.current || [], newSelected)) return
        streamsRef.current = newSelected
        setSelectedStreams(newSelected)
        if (!newSelected.length && !collegesRef.current?.length) {
            setTabData(emptyTabState()); setTabTotal(emptyTabNums()); setSummary(null); return
        }
        resetAndFetch(collegesRef.current, newSelected, searchRef.current, limitRef.current)
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        setSearch(val)
        searchRef.current = val
        clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(() => {
            resetAndFetch(collegesRef.current, streamsRef.current, val, limitRef.current)
        }, 400)
    }

    const handleTabSelect = (tab: TabKey) => {
        if (tab === activeTab) return
        activeTabRef.current = tab
        setActiveTab(tab)
        if (!tabData[tab].length) {
            fetchTab(tab, 1, limitRef.current, searchRef.current, collegesRef.current, streamsRef.current)
        }
    }

    const handlePageChange = (p: number) => {
        if (p === tabPage[activeTab]) return
        fetchTab(activeTab, p, limitRef.current, searchRef.current, collegesRef.current, streamsRef.current)
    }

    const handleLimitChange = (l: number) => {
        if (l === limitRef.current) return
        limitRef.current = l; setLimit(l)
        setTabPage({ both_matched: 1, only_fingerprint: 1, only_facial: 1, none_matched: 1, absent: 1 })
        setTabData(emptyTabState())
        fetchTab(activeTab, 1, l, searchRef.current, collegesRef.current, streamsRef.current)
    }

    const downloadTabCSV = async () => {
        setCsvLoading(true)
        try {
            const payload = {
                colleges: collegesRef.current?.map((d: any) => d?.value) || [],
                streams: streamsRef.current?.map((d: any) => d?.value) || [],
            }
            const res = await ClientService.downloadReverificationTabCSV(payload, activeTab, searchRef.current)
            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `reverification_${activeTab}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`
            link.click()
            URL.revokeObjectURL(url)
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to download CSV')
        } finally {
            setCsvLoading(false)
        }
    }

    useEffect(() => { fetchColleges(); getAllStreams() }, [])
    useEffect(() => () => clearTimeout(searchTimer.current), [])

    const hasFilters = !!(selectedCollege?.length || selectedStreams?.length)
    const activeTabMeta = TABS.find(t => t.key === activeTab)!

    const getCount = (tab: typeof TABS[0]) =>
        summary ? (summary[tab.summaryKey] ?? tabTotal[tab.key]) : tabTotal[tab.key]

    return (
        <div style={{ marginTop: 28, marginBottom: 40 }}>
            {/* ── Filter Bar ── */}
            <div style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                padding: '20px 24px',
                marginBottom: 20,
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: colors.text.secondary, textTransform: 'uppercase' }}>Records</p>
                        <h5 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: colors.text.primary }}>Reverifications</h5>
                    </div>
                    <button
                        onClick={() => fetchTab(activeTab, tabPage[activeTab], limitRef.current, searchRef.current, collegesRef.current, streamsRef.current)}
                        title="Refresh"
                        style={{ background: 'none', border: `1px solid #e5e7eb`, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: colors.text.secondary, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                    >
                        <FontAwesomeIcon icon={faRotateRight} />
                    </button>
                </div>

                <Row className="g-3 align-items-end">
                    <Col md={5}>
                        <label style={labelStyle}>Select Venue</label>
                        <Select
                            value={selectedCollege}
                            options={[{ label: 'All Venues', value: 'All' }, ...(colleges || [])]}
                            placeholder="Choose one or more venues..."
                            onChange={handleCollegesChange}
                            isClearable
                            isMulti
                            styles={selectStyles(colors)}
                        />
                    </Col>
                    {/* <Col md={4}>
                        <label style={labelStyle}>Filter by Stream</label>
                        <Select
                            value={selectedStreams}
                            options={[{ label: 'All Streams', value: 'All' }, ...(streams || [])]}
                            placeholder="All streams"
                            onChange={handleStreamChange}
                            isClearable
                            isMulti
                            styles={selectStyles(colors)}
                        />
                    </Col> */}
                    <Col md={3}>
                        <label style={labelStyle}>
                            Search&nbsp;
                            <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>
                                name · CET no. · CAP no.
                            </span>
                        </label>
                        <div style={{ position: 'relative' }}>
                            <FontAwesomeIcon
                                icon={faSearch}
                                style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13, pointerEvents: 'none' }}
                            />
                            <input
                                type="text"
                                placeholder="Search candidates..."
                                value={search}
                                onChange={handleSearchChange}
                                disabled={!hasFilters}
                                style={{
                                    width: '100%',
                                    paddingLeft: 32,
                                    paddingRight: 12,
                                    paddingTop: 8,
                                    paddingBottom: 8,
                                    borderRadius: 8,
                                    border: `1px solid ${hasFilters ? colors.border.secondary : '#e5e7eb'}`,
                                    fontSize: 13,
                                    outline: 'none',
                                    background: hasFilters ? '#fff' : '#f9fafb',
                                    color: colors.text.primary,
                                    transition: 'border-color 0.15s',
                                }}
                                onFocus={e => { e.target.style.borderColor = colors.primary.main }}
                                onBlur={e => { e.target.style.borderColor = colors.border.secondary }}
                            />
                        </div>
                    </Col>
                </Row>

                {/* Summary pills — show once data loads */}
                {summary && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                        {TABS.map(t => (
                            <span
                                key={t.key}
                                onClick={() => handleTabSelect(t.key)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '4px 12px', borderRadius: 20,
                                    background: activeTab === t.key ? `${t.accent}15` : '#f3f4f6',
                                    border: `1px solid ${activeTab === t.key ? t.accent : 'transparent'}`,
                                    fontSize: 12, fontWeight: 600,
                                    color: activeTab === t.key ? t.accent : '#6b7280',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.accent, display: 'inline-block' }} />
                                {t.label}: {getCount(t).toLocaleString()}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Tab + Table Panel ── */}
            <div style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                overflow: 'hidden',
            }}>
                {/* Tab bar */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e5e7eb',
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    background: '#fafafa',
                }}>
                    {TABS.map(t => {
                        const active = t.key === activeTab
                        return (
                            <button
                                key={t.key}
                                onClick={() => handleTabSelect(t.key)}
                                style={{
                                    flexShrink: 0,
                                    padding: '14px 20px',
                                    border: 'none',
                                    borderBottom: active ? `2px solid ${t.accent}` : '2px solid transparent',
                                    background: active ? '#fff' : 'transparent',
                                    fontWeight: active ? 700 : 500,
                                    fontSize: 13,
                                    color: active ? t.accent : '#6b7280',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    transition: 'all 0.15s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <span style={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    background: active ? t.accent : '#d1d5db',
                                    display: 'inline-block', transition: 'background 0.15s',
                                }} />
                                {t.label}
                                <span style={{
                                    background: active ? `${t.accent}18` : '#f3f4f6',
                                    color: active ? t.accent : '#9ca3af',
                                    borderRadius: 12, padding: '1px 8px', fontSize: 11, fontWeight: 700,
                                }}>
                                    {getCount(t).toLocaleString()}
                                </span>
                            </button>
                        )
                    })}
                </div>

                {/* Toolbar row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: 13, color: colors.text.secondary }}>
                        {hasFilters
                            ? `${tabTotal[activeTab].toLocaleString()} record${tabTotal[activeTab] !== 1 ? 's' : ''} · page ${tabPage[activeTab]} of ${Math.ceil(tabTotal[activeTab] / limit) || 1}`
                            : 'Select a venue to load records'}
                    </span>
                    {hasFilters && (
                        <button
                            onClick={downloadTabCSV}
                            disabled={csvLoading || !tabTotal[activeTab]}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 7,
                                padding: '7px 14px', borderRadius: 8,
                                border: `1px solid ${activeTabMeta.accent}`,
                                background: csvLoading ? '#f3f4f6' : `${activeTabMeta.accent}12`,
                                color: csvLoading ? '#9ca3af' : activeTabMeta.accent,
                                fontSize: 12, fontWeight: 600, cursor: csvLoading || !tabTotal[activeTab] ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s',
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            {csvLoading ? 'Downloading...' : 'Export CSV'}
                        </button>
                    )}
                </div>

                {/* Content */}
                <div style={{ padding: '0 20px 20px' }}>
                    {loading ? (
                        <div style={{ padding: '48px 0' }}><CetLoader /></div>
                    ) : !hasFilters ? (
                        <EmptyState message="Please select a venue to view reverification data." />
                    ) : (
                        <>
                            <ReverificationTable
                                tableData={tabData[activeTab]}
                                pageOffset={(tabPage[activeTab] - 1) * limit}
                            />
                            <TablePaginationComponent
                                itemsCount={tabTotal[activeTab]}
                                itemsPerPage={limit}
                                currentPage={tabPage[activeTab]}
                                setCurrentPage={handlePageChange}
                                setItemsPerPage={handleLimitChange}
                                resetPageOnItemsPerPageChange={false}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
}

const selectStyles = (colors: any) => ({
    control: (base: any, state: any) => ({
        ...base,
        borderRadius: 8,
        border: `1px solid ${state.isFocused ? colors.primary.main : colors.border.secondary}`,
        boxShadow: state.isFocused ? `0 0 0 3px ${colors.primary.main}18` : 'none',
        fontSize: 13,
        minHeight: 38,
        '&:hover': { borderColor: colors.primary.main },
    }),
    option: (base: any, state: any) => ({
        ...base,
        fontSize: 13,
        backgroundColor: state.isSelected ? colors.primary.main : state.isFocused ? colors.background.secondary : '#fff',
        color: state.isSelected ? '#fff' : colors.text.primary,
    }),
    multiValue: (base: any) => ({
        ...base,
        backgroundColor: `${colors.primary.main}15`,
        borderRadius: 6,
    }),
    multiValueLabel: (base: any) => ({
        ...base,
        color: colors.primary.dark,
        fontSize: 12,
        fontWeight: 600,
    }),
    multiValueRemove: (base: any) => ({
        ...base,
        color: colors.primary.main,
        ':hover': { backgroundColor: `${colors.primary.main}25`, color: colors.primary.dark },
        borderRadius: '0 6px 6px 0',
    }),
})

const EmptyState = ({ message }: { message: string }) => (
    <div style={{ textAlign: 'center', padding: '56px 0' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <FontAwesomeIcon icon={faUniversity} style={{ fontSize: 28, color: '#d1d5db' }} />
        </div>
        <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>{message}</p>
    </div>
)
